import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';
import type { ITelemetryDeviceInfo } from '@/service/vpc/types/IUnit';

export const getTelemetryDevices = (
  telemetryDeviceInfo?: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null
): ITelemetryDeviceInfo[] => {
  if (!telemetryDeviceInfo) return [];
  return Array.isArray(telemetryDeviceInfo) ? telemetryDeviceInfo : [telemetryDeviceInfo];
};

export const hasTelemetryDeviceType = (
  telemetryDeviceInfo: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null | undefined,
  deviceType: string
): boolean => {
  const normalizedDeviceType = deviceType.trim().toLowerCase();
  return getTelemetryDevices(telemetryDeviceInfo).some(
    (device) => device.device?.trim().toLowerCase() === normalizedDeviceType
  );
};

export const hasOkTelemetryDeviceType = (
  telemetryDeviceInfo: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null | undefined,
  deviceType: string
): boolean => {
  const normalizedDeviceType = deviceType.trim().toLowerCase();
  return getTelemetryDevices(telemetryDeviceInfo).some(
    (device) =>
      device.device?.trim().toLowerCase() === normalizedDeviceType &&
      device.communicationStatus?.trim().toLowerCase() === 'ok'
  );
};

export const getTelemetryDeviceChassisId = (
  telemetryDeviceInfo: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null | undefined,
  deviceType: string
): string | undefined => {
  const normalizedDeviceType = deviceType.trim().toLowerCase();
  const candidates = getTelemetryDevices(telemetryDeviceInfo).filter(
    (device) => device.device?.trim().toLowerCase() === normalizedDeviceType && device.chassisId != null
  );
  // A unit can report several chassis ids for one device; the VCTRL one is the boat itself
  // (the backend does the same, AB#1616879), so prefer it over an engine chassis.
  const telemetryDevice =
    candidates.find((device) => device.chassisId?.trim().toUpperCase().startsWith('VCTRL')) ?? candidates[0];

  return telemetryDevice?.chassisId ?? undefined;
};

export const hasEcWithOkCommStatus = (
  telemetryDeviceInfo: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null | undefined
): boolean => {
  return hasOkTelemetryDeviceType(telemetryDeviceInfo, 'EC');
};

// An "Easy Connect" unit has an EC-family telemetry device — either 'EC' or 'EC_Wifi'
// — with OK communication status, AND no TGW device. Combo units (TGW + EC) keep their
// digital services as before, so they are NOT treated as Easy Connect units regardless
// of the TGW's communication status. Used to hide EC service provisioning eligibility.
export const isEasyConnectUnit = (
  telemetryDeviceInfo: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null | undefined
): boolean => {
  if (hasTelemetryDeviceType(telemetryDeviceInfo, 'TGW')) return false;
  return hasEcWithOkCommStatus(telemetryDeviceInfo) || hasOkTelemetryDeviceType(telemetryDeviceInfo, 'EC_Wifi');
};

export const hasActiveService = (serviceStatuses: ITelemetryService[] | null | undefined): boolean => {
  if (!serviceStatuses) return false;
  return serviceStatuses.some((service) => service.status === 'Active');
};

export const getConnectivityStatusVariant = (
  status: string | null | undefined
): 'success' | 'neutral' | null => {
  const normalized = status?.trim().toLowerCase();
  if (normalized === 'online') return 'success';
  if (normalized === 'offline') return 'neutral';
  return null;
};
